import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  useTheme,
  Paper,
  Stack,
  Button,
} from '@mui/material';
import {
  Business,
  Description,
  DateRange,
  Work,
  AttachMoney,
  Refresh,
  TrendingUp,
  FileDownload,
  Schedule,
  PlayArrow,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import MyModal from '../MyModal/MyModal';
import { contractDetailsModalStyles } from './ContractDetailsModal.styles';
import s3Service from '../../services/s3Service';

const ContractDetailsModal = ({ open, onClose, contract }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!contract) {
    return null;
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(t('common.locale', { defaultValue: 'tr-TR' }), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleDownloadPdf = async (pdfKey, fileName) => {
    try {
      const signedUrl = await s3Service.getSignedUrl(pdfKey);
      if (signedUrl) {
        const link = document.createElement('a');
        link.href = signedUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('PDF indirme hatası:', error);
    }
  };

  const contractInfoItems = [
    {
      icon: <Description />,
      label: t('contractDetails.subject'),
      value: contract.contract_subject?.name || '-',
      id: contract.contract_subject?.id,
      isProjectBased: contract.contract_subject?.is_project_based,
    },
    {
      icon: <Business />,
      label: t('contractDetails.firm'),
      value: contract.firm?.firm_name || contract.firm?.firm_short_name || '-',
      id: contract.firm?.id,
    },
    {
      icon: <Work />,
      label: t('contractDetails.scopeOfWork'),
      value: contract.scope_of_work?.name || '-',
      id: contract.scope_of_work?.id,
    },
    {
      icon: <AttachMoney />,
      label: t('contractDetails.pricingMethod'),
      value: contract.pricing_method?.name || '-',
      id: contract.pricing_method?.id,
    },
    {
      icon: <DateRange />,
      label: t('contractDetails.signingDate'),
      value: formatDate(contract.contract_signing_date),
    },
    {
      icon: <DateRange />,
      label: t('contractDetails.workStartDate'),
      value: formatDate(contract.work_start_date),
    },
  ];

  const additionalInfoItems = [
    {
      icon: <Refresh />,
      label: t('contractDetails.autoRenewal'),
      value: contract.auto_renewal ? t('common.yes') : t('common.no'),
      color: contract.auto_renewal ? 'success' : 'default',
    },
    {
      icon: <TrendingUp />,
      label: t('contractDetails.inflationClause'),
      value: contract.inflation_clause ? t('common.yes') : t('common.no'),
      color: contract.inflation_clause ? 'success' : 'default',
    },
    {
      icon: <PlayArrow />,
      label: t('contractDetails.workStartedWithoutContract'),
      value: contract.work_started_without_contract ? t('common.yes') : t('common.no'),
      color: contract.work_started_without_contract ? 'warning' : 'default',
    },
    {
      icon: <Schedule />,
      label: t('contractDetails.renewalPeriod'),
      value: contract.renewal_period || '-',
    },
    {
      icon: <DateRange />,
      label: t('contractDetails.protocolStartDate'),
      value: formatDate(contract.protocol_start_date),
    },
    {
      icon: <DateRange />,
      label: t('contractDetails.protocolEndDate'),
      value: formatDate(contract.protocol_end_date),
    },
  ];

  const fileInfoItems = [
    {
      icon: <FileDownload />,
      label: t('contractDetails.contractPdf'),
      value: contract.contract_pdf_key ? `${t('common.available')} (${contract.contract_pdf_size || 0} KB)` : t('common.notAvailable'),
      color: contract.contract_pdf_key ? 'success' : 'default',
      pdfKey: contract.contract_pdf_key,
      fileName: 'contract.pdf',
      title: t('contractDetails.contractPdf'),
    },
    {
      icon: <FileDownload />,
      label: t('contractDetails.protocolPdf'),
      value: contract.protocol_pdf_key ? `${t('common.available')} (${contract.protocol_pdf_size || 0} KB)` : t('common.notAvailable'),
      color: contract.protocol_pdf_key ? 'success' : 'default',
      pdfKey: contract.protocol_pdf_key,
      fileName: 'protocol.pdf',
      title: t('contractDetails.protocolPdf'),
    },
  ];

  const renderInfoItem = (item, index) => (
    <Grid size={{ xs: 12, sm: 6 }} key={index}>
      <Box sx={contractDetailsModalStyles.infoItem}>
        <Box sx={contractDetailsModalStyles.infoIcon}>
          {item.icon}
        </Box>
        <Box sx={contractDetailsModalStyles.infoContent}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={contractDetailsModalStyles.infoLabel}
          >
            {item.label}
            {item.id && (
              <Typography
                component="span"
                variant="caption"
                color="text.disabled"
                sx={{ ml: 1 }}
              >
                (ID: {item.id})
              </Typography>
            )}
          </Typography>
          <Typography
            variant="body2"
            color={item.color === 'success' ? 'success.main' : item.color === 'warning' ? 'warning.main' : 'text.primary'}
            sx={contractDetailsModalStyles.infoValue}
          >
            {item.value}
          </Typography>
          {item.isProjectBased !== undefined && (
            <Box sx={{ mt: 0.5 }}>
              <Chip
              onClick={() => {
              }}
                label={item.isProjectBased ? t('contractDetails.projectBased') : t('contractDetails.notProjectBased')}
                size="small"
                color={item.isProjectBased ? 'success' : 'default'}
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: '20px' }}
              />
            </Box>
          )}
          {item.pdfKey && (
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileDownload />}
                onClick={() => handleDownloadPdf(item.pdfKey, item.fileName)}
                sx={{ fontSize: '0.75rem' }}
              >
                {t('common.download')}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Grid>
  );

  return (
    <MyModal
      open={open}
      onClose={onClose}
      title={t('contractDetails.title')}
      maxWidth="lg"
      fullWidth
      disableBackdropClose
      disableEscapeKeyDown
    >
      <Box sx={contractDetailsModalStyles.container}>
        <Paper elevation={2} sx={contractDetailsModalStyles.statusSection}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography
              variant="h6"
              color="text.primary"
              sx={contractDetailsModalStyles.sectionTitle}
            >
              {t('contractDetails.status')}
            </Typography>
            <Chip
              label={contract.work_status?.name || '-'}
              color="primary"
              variant="filled"
              sx={contractDetailsModalStyles.statusChip}
              onClick={() => {
              }}
            />
          </Stack>
        </Paper>

        <Divider sx={contractDetailsModalStyles.divider} />

        <Box sx={contractDetailsModalStyles.infoSection}>
          <Typography
            variant="h6"
            color="text.primary"
            sx={contractDetailsModalStyles.sectionTitle}
          >
            {t('contractDetails.information')}
          </Typography>
          <Grid container spacing={3}>
            {contractInfoItems.map(renderInfoItem)}
          </Grid>
        </Box>

        <Divider sx={contractDetailsModalStyles.divider} />
        <Box sx={contractDetailsModalStyles.infoSection}>
          <Typography
            variant="h6"
            color="text.primary"
            sx={contractDetailsModalStyles.sectionTitle}
          >
            {t('contractDetails.additionalInfo')}
          </Typography>
          <Grid container spacing={3}>
            {additionalInfoItems.map(renderInfoItem)}
          </Grid>
        </Box>

        <Divider sx={contractDetailsModalStyles.divider} />
        <Box sx={contractDetailsModalStyles.infoSection}>
          <Typography
            variant="h6"
            color="text.primary"
            sx={contractDetailsModalStyles.sectionTitle}
          >
            {t('contractDetails.files')}
          </Typography>
          <Grid container spacing={3}>
            {fileInfoItems.map(renderInfoItem)}
          </Grid>
        </Box>

        {/* İlgili İşler: sözleşmeye baglı jobs listesi. Modal İşler sayfasindan
            veya Fatura/Ciro sayfalarindan açılabildigi icin contract.jobs
            GET_JOBS ve GET_CONTRACTS_QUERY'den geliyor (Task 1). */}
        {Array.isArray(contract.jobs) && contract.jobs.length > 0 && (
          <>
            <Divider sx={contractDetailsModalStyles.divider} />
            <Box sx={contractDetailsModalStyles.infoSection}>
              <Typography
                variant="h6"
                color="text.primary"
                sx={contractDetailsModalStyles.sectionTitle}
              >
                {t('contractDetails.relatedJobs', 'İlgili İşler')}
                <Chip
                  onClick={() => {}}
                  label={contract.jobs.length}
                  size="small"
                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                />
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {contract.jobs.map((job) => (
                  <Chip
                    key={job.id}
                    onClick={() => {}}
                    icon={<Work fontSize="small" />}
                    label={
                      job.job_title
                        ? (job.job_status
                          ? `${job.job_title} • ${t(`jobs.jobStatuses.${job.job_status}`, job.job_status)}`
                          : job.job_title)
                        : `#${job.id}`
                    }
                    variant="outlined"
                    color="primary"
                    size="small"
                    sx={{ maxWidth: 360 }}
                  />
                ))}
              </Stack>
            </Box>
          </>
        )}

        {contract.comment && (
          <>
            <Divider sx={contractDetailsModalStyles.divider} />
            <Box sx={contractDetailsModalStyles.infoSection}>
              <Typography
                variant="h6"
                color="text.primary"
                sx={contractDetailsModalStyles.sectionTitle}
              >
                {t('contractDetails.comment')}
              </Typography>
              <Paper elevation={1} sx={contractDetailsModalStyles.commentSection}>
                <Typography variant="body2" color="text.primary">
                  {contract.comment}
                </Typography>
              </Paper>
            </Box>
          </>
        )}

        <Divider sx={contractDetailsModalStyles.divider} />
        <Box sx={contractDetailsModalStyles.infoSection}>
          <Typography
            variant="h6"
            color="text.primary"
            sx={contractDetailsModalStyles.sectionTitle}
          >
            {t('contractDetails.metadata')}
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={contractDetailsModalStyles.infoItem}>
                <Box sx={contractDetailsModalStyles.infoIcon}>
                  <DateRange />
                </Box>
                <Box sx={contractDetailsModalStyles.infoContent}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={contractDetailsModalStyles.infoLabel}
                  >
                    {t('contractDetails.createdAt')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={contractDetailsModalStyles.infoValue}
                  >
                    {formatDate(contract.created_at)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={contractDetailsModalStyles.infoItem}>
                <Box sx={contractDetailsModalStyles.infoIcon}>
                  <DateRange />
                </Box>
                <Box sx={contractDetailsModalStyles.infoContent}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={contractDetailsModalStyles.infoLabel}
                  >
                    {t('contractDetails.updatedAt')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={contractDetailsModalStyles.infoValue}
                  >
                    {formatDate(contract.updated_at)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Yenileme Geçmişi: contract_renewals tablosundan gelen kayıtlar.
            new_monthly_amount fiyat değişikliği bilgisini, ciro hesabı bunu
            ay-bazlı sliding ile kullanıyor (computeQuarterAmount). */}
        {Array.isArray(contract.renewals) && contract.renewals.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Refresh fontSize="small" color="primary" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t('contracts.renewal.history', 'Geçmiş Yenilemeler')}
              </Typography>
              <Chip
                label={contract.renewals.length}
                size="small"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            </Box>
            <Stack spacing={1}>
              {[...contract.renewals]
                .sort((a, b) => {
                  const ta = a.renewal_start_date ? new Date(a.renewal_start_date).getTime() : 0;
                  const tb = b.renewal_start_date ? new Date(b.renewal_start_date).getTime() : 0;
                  return tb - ta;
                })
                .map((r) => (
                  <Paper key={r.id} variant="outlined" sx={{ p: 1.25 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {r.renewal_start_date} {String.fromCharCode(8594)} {r.renewal_end_date}
                      </Typography>
                      {r.new_monthly_amount != null && (
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={`${Number(r.new_monthly_amount).toLocaleString('tr-TR')} ${String.fromCharCode(8378)}/ay`}
                          sx={{ height: 22, fontSize: '0.72rem' }}
                        />
                      )}
                    </Box>
                    {r.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {r.description}
                      </Typography>
                    )}
                  </Paper>
                ))}
            </Stack>
          </Box>
        )}
      </Box>
    </MyModal>
  );
};

export default ContractDetailsModal;
